import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Layers3,
  Target,
  Shield,
  Database,
  Server,
  Workflow,
  Sparkles,
  Users,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  BookOpen,
} from 'lucide-react';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend } from 'recharts';
import {
  cadreBZMappingStats,
  cadreBZSimplePiliers,
  cadreBZTransversales,
  getSimplePilierName,
  getTransversaleName,
  livrablesAvecCadreSimple,
  tactiquesAvecCadreSimple,
  type SimpleCadreBZPilierId,
} from '../data/cadreBZMapping';

interface EbaucheCadreBZModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Pilier = {
  id: number;
  nom: string;
  icon: typeof Target;
  couleur: string;
  intention: string;
  pourquoiCritique: string;
  concretementBZ: string;
  erreursFrequentes: string[];
  couvertures: string[];
  referentiels: string[];
  niveaux: { niveau: number; label: string; description: string }[];
  aFaireMaintenant: string[];
  preuvesVisibles: string[];
  actuel: number;
  cible: number;
};

const piliersCadreBZ: Pilier[] = [
  {
    id: 1,
    nom: 'Gouvernance, stratégie & organisation',
    icon: Target,
    couleur: 'from-fuchsia-500 to-violet-500',
    intention: 'Donner un cap clair à la TI et structurer les décisions, les rôles et les mécanismes de pilotage.',
    pourquoiCritique: 'Sans gouvernance claire, le reste devient réactif, incohérent et difficile à prioriser.',
    concretementBZ: 'Vision TI, comités, rôles, arbitrages, politiques, alignement business et pilotage multi-niveaux.',
    erreursFrequentes: [
      'Confondre gouvernance et bureaucratie',
      'Décisions prises sans trace durable',
      'Rôles flous ou doublons de responsabilité',
      'Priorités qui changent selon le bruit du moment',
    ],
    couvertures: [
      'Vision TI et alignement d’affaires',
      'Rôles, responsabilités, RACI et comités',
      'Décisions, priorisation et arbitrages',
      'Politiques, règles de gestion et cadre organisationnel',
    ],
    referentiels: ['COBIT', 'ITIL 4', 'PRIsME'],
    niveaux: [
      { niveau: 0, label: 'Ad hoc', description: 'Décisions réactives, rôles implicites, gouvernance peu visible.' },
      { niveau: 1, label: 'Émergent', description: 'Vision de base et quelques acteurs clés, mais peu de structure.' },
      { niveau: 2, label: 'Structuré', description: 'Comités, rôles et priorités commencent à être formalisés.' },
      { niveau: 3, label: 'Piloté', description: 'Décisions suivies, RACI plus clair, gouvernance active.' },
      { niveau: 4, label: 'Maîtrisé', description: 'Gouvernance vivante, alignée aux objectifs et intégrée aux opérations.' },
      { niveau: 5, label: 'Référence', description: 'La gouvernance devient un avantage compétitif et culturel.' },
    ],
    aFaireMaintenant: [
      'Nommer les décisions structurantes du cadre',
      'Valider les owners par pilier',
      'Définir une cadence de revue du cadre BZ',
    ],
    preuvesVisibles: ['RACI', 'Registre de décisions', 'Comités actifs', 'Priorités formalisées'],
    actuel: 3.2,
    cible: 4.4,
  },
  {
    id: 2,
    nom: 'Architecture, infrastructure & cloud',
    icon: Server,
    couleur: 'from-sky-500 to-blue-500',
    intention: 'Maîtriser les fondations technologiques qui soutiennent la performance, la résilience et la croissance.',
    pourquoiCritique: 'Sans base technique saine, les services, la sécurité et les applications héritent de la fragilité.',
    concretementBZ: 'Architecture TI, plateformes, réseaux, hébergement, infonuagique, capacité, redondance et continuité technique.',
    erreursFrequentes: [
      'Tout noyer dans “les opérations” sans lecture architecture',
      'Traiter le cloud comme un simple hébergement',
      'Ignorer les dépendances et la dette technique',
      'Construire sans logique de résilience',
    ],
    couvertures: [
      'Architecture technologique',
      'Serveurs, réseaux, postes et plateformes',
      'Cloud, hébergement et redondance',
      'Capacité, performance et continuité technique',
    ],
    referentiels: ['ITIL 4', 'COBIT', 'PRIsME'],
    niveaux: [
      { niveau: 0, label: 'Invisible', description: 'Peu de vision d’ensemble de l’architecture et des dépendances.' },
      { niveau: 1, label: 'Réactif', description: 'Infrastructure opérée, mais peu structurée ou documentée.' },
      { niveau: 2, label: 'Cartographié', description: 'Standards et architecture de base commencent à exister.' },
      { niveau: 3, label: 'Fiabilisé', description: 'Capacité, redondance et architecture sont gérées activement.' },
      { niveau: 4, label: 'Optimisé', description: 'Les choix techniques supportent activement la stratégie et la résilience.' },
      { niveau: 5, label: 'Exemplaire', description: 'Infrastructure et cloud deviennent un levier fort de différenciation.' },
    ],
    aFaireMaintenant: [
      'Clarifier la vision architecture cible',
      'Documenter les dépendances critiques',
      'Mettre la redondance et la continuité dans le discours standard',
    ],
    preuvesVisibles: ['Schéma d’architecture', 'Standards cloud', 'Plan de continuité', 'Redondance documentée'],
    actuel: 3.0,
    cible: 4.3,
  },
  {
    id: 3,
    nom: 'Applications, données & intégrations',
    icon: Database,
    couleur: 'from-emerald-500 to-teal-500',
    intention: 'Donner une lecture claire du patrimoine applicatif et informationnel, incluant les flux et les responsabilités.',
    pourquoiCritique: 'Les systèmes métiers et les données sont au cœur de la valeur; s’ils sont mal gouvernés, la TI avance à l’aveugle.',
    concretementBZ: 'Applications, systèmes métiers, données, qualité d’information, flux, intégrations et ownership applicatif.',
    erreursFrequentes: [
      'Voir les applications seulement comme des outils isolés',
      'Oublier l’ownership des données',
      'Traiter les intégrations comme des détails techniques',
      'Avoir des données critiques sans gouvernance claire',
    ],
    couvertures: [
      'Portefeuille applicatif et systèmes métiers',
      'Données, qualité, cycle de vie et classification',
      'Intégrations, interopérabilité et flux',
      'Ownership applicatif et dépendances fonctionnelles',
    ],
    referentiels: ['COBIT', 'ITIL 4', 'NIST', 'PRIsME'],
    niveaux: [
      { niveau: 0, label: 'Opaque', description: 'Patrimoine applicatif et données peu connus ou peu maîtrisés.' },
      { niveau: 1, label: 'Inventorié partiellement', description: 'Quelques systèmes et données critiques identifiés.' },
      { niveau: 2, label: 'Structuré', description: 'Applications, données et intégrations commencent à être cartographiées.' },
      { niveau: 3, label: 'Gouverné', description: 'Ownership, qualité et dépendances sont mieux encadrés.' },
      { niveau: 4, label: 'Aligné', description: 'Le patrimoine app/data supporte activement le pilotage et la sécurité.' },
      { niveau: 5, label: 'Levier stratégique', description: 'Applications et données deviennent un véritable avantage structuré.' },
    ],
    aFaireMaintenant: [
      'Rendre explicite le bloc Applications & Données dans le cadre',
      'Définir les owners clés',
      'Relier les intégrations aux risques et à la gouvernance',
    ],
    preuvesVisibles: ['Inventaire applicatif', 'Propriétaires identifiés', 'Flux documentés', 'Données classifiées'],
    actuel: 2.8,
    cible: 4.2,
  },
  {
    id: 4,
    nom: 'Services, opérations & expérience utilisateur',
    icon: Workflow,
    couleur: 'from-amber-500 to-orange-500',
    intention: 'Assurer une prestation TI fiable, mesurable et compréhensible du point de vue utilisateur et client.',
    pourquoiCritique: 'C’est là que la valeur perçue se joue au quotidien.',
    concretementBZ: 'Support TI, incidents, demandes, changements, disponibilité, performance, continuité de service et expérience utilisateur.',
    erreursFrequentes: [
      'Faire du support sans logique de service',
      'Gérer les incidents sans apprentissage durable',
      'Mesurer la performance interne plutôt que la valeur perçue',
      'Négliger l’expérience utilisateur',
    ],
    couvertures: [
      'Catalogue de services et support TI',
      'Incidents, demandes, changements et problèmes',
      'Disponibilité, performance et continuité de service',
      'Qualité perçue et expérience utilisateur',
    ],
    referentiels: ['ITIL 4', 'PRIsME'],
    niveaux: [
      { niveau: 0, label: 'Réactif pur', description: 'Les opérations répondent aux urgences sans structure de service claire.' },
      { niveau: 1, label: 'Basique', description: 'Quelques pratiques de support existent, mais sont peu formalisées.' },
      { niveau: 2, label: 'Standardisé', description: 'Incidents, demandes et changements sont mieux encadrés.' },
      { niveau: 3, label: 'Piloté', description: 'Les services sont suivis et la prestation devient plus prévisible.' },
      { niveau: 4, label: 'Orienté valeur', description: 'Le service est pensé selon l’impact, la qualité et l’expérience.' },
      { niveau: 5, label: 'Excellence de service', description: 'Les opérations deviennent une signature forte de fiabilité.' },
    ],
    aFaireMaintenant: [
      'Aligner le langage service vs support',
      'Clarifier les indicateurs de qualité perçue',
      'Lier les opérations aux autres piliers du cadre',
    ],
    preuvesVisibles: ['Catalogue de services', 'SLA/indicateurs', 'Changements suivis', 'Expérience mesurée'],
    actuel: 3.4,
    cible: 4.5,
  },
  {
    id: 5,
    nom: 'Cybersécurité, risque & conformité',
    icon: Shield,
    couleur: 'from-rose-500 to-red-500',
    intention: 'Protéger l’organisation, structurer le risque et rendre la conformité opérable.',
    pourquoiCritique: 'La sécurité n’est pas un module à part; c’est une condition de confiance et de résilience.',
    concretementBZ: 'Posture cyber, risques, contrôles, conformité, accès, protection, détection, réponse et reprise.',
    erreursFrequentes: [
      'Réduire la cyber à de la technique',
      'Parler conformité sans risque réel',
      'Ajouter la sécurité à la fin',
      'Ne pas documenter les arbitrages de risque',
    ],
    couvertures: [
      'Gouvernance cyber et posture de sécurité',
      'Risque, contrôles et conformité',
      'Protection, détection, réponse et reprise',
      'Identités, accès et protection des données',
    ],
    referentiels: ['NIST CSF 2.0', 'COBIT', 'ISO 27001', 'PRIsME'],
    niveaux: [
      { niveau: 0, label: 'Réactif', description: 'Sécurité peu structurée, dépendante des urgences.' },
      { niveau: 1, label: 'Conscient', description: 'Risques et enjeux connus, mais peu cadrés.' },
      { niveau: 2, label: 'Encadré', description: 'Contrôles et pratiques de base existent et sont suivis.' },
      { niveau: 3, label: 'Gouverné', description: 'Risque, posture et conformité sont gérés activement.' },
      { niveau: 4, label: 'Résilient', description: 'La sécurité influence les décisions et renforce la confiance.' },
      { niveau: 5, label: 'Exemplaire', description: 'La posture cyber devient une force reconnue et maîtrisée.' },
    ],
    aFaireMaintenant: [
      'Garder NIST comme colonne forte du pilier',
      'Rendre explicites les choix de risque',
      'Montrer les liens entre cyber, données et gouvernance',
    ],
    preuvesVisibles: ['Registre de risques', 'Comité sécurité', 'Contrôles documentés', 'Plans de réponse'],
    actuel: 3.6,
    cible: 4.6,
  },
  {
    id: 6,
    nom: 'Transformation, innovation & IA',
    icon: Sparkles,
    couleur: 'from-violet-500 to-indigo-500',
    intention: 'Faire évoluer les capacités TI de manière utile, structurée et sécuritaire.',
    pourquoiCritique: 'Sans évolution, le cadre devient figé; sans discipline, l’innovation devient du bruit.',
    concretementBZ: 'Transformation numérique, automatisation, IA, expérimentation contrôlée, amélioration de la valeur et différenciation.',
    erreursFrequentes: [
      'Faire de l’innovation gadget',
      'Lancer trop d’initiatives à la fois',
      'Confondre automatisation et valeur',
      'Introduire l’IA sans gouvernance ni objectifs clairs',
    ],
    couvertures: [
      'Transformation numérique et évolution du modèle TI',
      'Innovation structurée et expérimentation',
      'IA, automatisation et gains de productivité',
      'Veille, modernisation et différenciation',
    ],
    referentiels: ['PRIsME', 'ITIL 4', 'NIST AI RMF'],
    niveaux: [
      { niveau: 0, label: 'Statique', description: 'Peu d’innovation structurée ou de capacité d’évolution.' },
      { niveau: 1, label: 'Ponctuel', description: 'Quelques essais isolés sans cadre stable.' },
      { niveau: 2, label: 'Canalisé', description: 'Innovation et IA commencent à être intégrées avec plus d’intention.' },
      { niveau: 3, label: 'Structuré', description: 'Les initiatives s’alignent avec la valeur et la gouvernance.' },
      { niveau: 4, label: 'Accélérateur', description: 'Transformation et IA deviennent des leviers concrets d’efficacité.' },
      { niveau: 5, label: 'Différenciateur', description: 'L’innovation est maîtrisée, utile et perçue comme une vraie force.' },
    ],
    aFaireMaintenant: [
      'Rendre l’IA utilitaire et non décorative',
      'Définir ce qui est innovation vs exploitation',
      'Garder une discipline de priorisation',
    ],
    preuvesVisibles: ['Roadmap innovation', 'Cas d’usage IA', 'Automatisations utiles', 'Décisions d’expérimentation'],
    actuel: 3.1,
    cible: 4.4,
  },
];

const transversales = [
  {
    id: 't1',
    nom: 'Gestion des actifs & parties prenantes',
    icon: Users,
    pourquoi: 'Cette dimension traverse tous les piliers, parce que les actifs, les owners, les parties prenantes et les dépendances existent partout.',
    couvre: [
      'Inventaire et classification des actifs',
      'Ownership, responsabilités et parties prenantes',
      'Dépendances entre actifs, services, applications et équipes',
      'Traçabilité du patrimoine TI et lecture commune',
    ],
    role: 'Servir de couche de structuration transversale entre l’infrastructure, les applications, les services, la sécurité et la gouvernance.',
  },
  {
    id: 't2',
    nom: 'Gestion de projet & amélioration continue',
    icon: TrendingUp,
    pourquoi: 'Cette dimension traverse tous les piliers, parce que les projets et l’amélioration continue sont la mécanique de progression du cadre.',
    couvre: [
      'Gestion de projet, jalons, tâches et priorités',
      'Portefeuille, gouvernance et exécution',
      'Leçons apprises, post-mortem et qualité',
      'Maturité, progression, optimisation et capacité de transformation',
    ],
    role: 'Faire évoluer chaque pilier de manière contrôlée, documentée et mesurable plutôt que créer un silo supplémentaire.',
  },
];

function OverviewTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-fuchsia-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Ébauche de cadre client BZ</h3>
        <p className="text-gray-300 text-center text-sm">
          Une proposition simple, pilotable et alignée à ITIL, COBIT, NIST et PRIsME.
        </p>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-indigo-400" />
          Intention du cadre
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Le but n’est pas de copier un référentiel de plus. Le but est de créer une grille BZ qui parle le langage terrain,
          respecte les grands standards et reste assez claire pour être utilisée en gouvernance, en diagnostic, en vente et en exécution.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="bg-white/5 rounded-lg p-4 border border-indigo-500/20">
            <p className="font-semibold text-fuchsia-300 mb-2">Ce que COBIT apporte</p>
            <p className="text-gray-300">Gouvernance, alignement, responsabilités, contrôles et discipline de pilotage.</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-indigo-500/20">
            <p className="font-semibold text-sky-300 mb-2">Ce que ITIL 4 apporte</p>
            <p className="text-gray-300">Gestion des services, création de valeur, exploitation, expérience et amélioration.</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-indigo-500/20">
            <p className="font-semibold text-rose-300 mb-2">Ce que NIST apporte</p>
            <p className="text-gray-300">Cybersécurité, risque, protection, résilience et confiance opérationnelle.</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-indigo-500/20">
            <p className="font-semibold text-emerald-300 mb-2">Ce que PRIsME apporte</p>
            <p className="text-gray-300">La structure BZ : comités, livrables, personnalisation, qualité, projets et reproductibilité.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-fuchsia-500/15 border border-fuchsia-500/30 rounded-lg p-4">
          <div className="text-sm text-fuchsia-300 font-medium">Dimensions principales</div>
          <div className="text-2xl font-bold text-fuchsia-200">6</div>
          <div className="text-xs text-fuchsia-300/80">Piliers structurants</div>
        </div>
        <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-lg p-4">
          <div className="text-sm text-emerald-300 font-medium">Dimensions transversales</div>
          <div className="text-2xl font-bold text-emerald-200">2</div>
          <div className="text-xs text-emerald-300/80">Capacités communes</div>
        </div>
        <div className="bg-indigo-500/15 border border-indigo-500/30 rounded-lg p-4">
          <div className="text-sm text-indigo-300 font-medium">Lecture recommandée</div>
          <div className="text-2xl font-bold text-indigo-200">6 + 2</div>
          <div className="text-xs text-indigo-300/80">Simple, vendable et pilotable</div>
        </div>
      </div>
    </div>
  );
}

export function PiliersTab() {
  const [expandedPilier, setExpandedPilier] = useState<number | null>(1);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Les 6 piliers principaux</h3>
        <p className="text-gray-300 text-center text-sm">
          Un cadre lisible, avec intention, portée, erreurs à éviter, niveaux de maturité et prochaines actions.
        </p>
      </div>

      <div className="space-y-4">
        {piliersCadreBZ.map((pilier) => {
          const Icon = pilier.icon;
          return (
            <div key={pilier.id} className="bg-white/5 rounded-xl border border-indigo-500/20 overflow-hidden">
              <button
                onClick={() => setExpandedPilier(expandedPilier === pilier.id ? null : pilier.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${pilier.couleur} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-lg font-bold text-white">{pilier.id}. {pilier.nom}</h4>
                    <p className="text-sm text-gray-400">{pilier.intention}</p>
                  </div>
                </div>
                <div className="text-gray-400 text-sm">{expandedPilier === pilier.id ? 'Réduire' : 'Développer'}</div>
              </button>

              {expandedPilier === pilier.id && (
                <div className="p-6 border-t border-indigo-500/20 space-y-5">
                  <div>
                    <h5 className="text-sm font-bold text-indigo-400 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Intention
                    </h5>
                    <p className="text-gray-300 text-sm leading-relaxed">{pilier.intention}</p>
                  </div>

                  <div>
                    <h5 className="text-sm font-bold text-yellow-400 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Pourquoi c&apos;est critique
                    </h5>
                    <p className="text-gray-300 text-sm leading-relaxed">{pilier.pourquoiCritique}</p>
                  </div>

                  <div>
                    <h5 className="text-sm font-bold text-emerald-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Concrètement chez BZ
                    </h5>
                    <p className="text-gray-300 text-sm leading-relaxed">{pilier.concretementBZ}</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h5 className="text-sm font-bold text-cyan-400 mb-2">Ce que ça couvre</h5>
                      <ul className="space-y-2">
                        {pilier.couvertures.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                            <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="text-sm font-bold text-purple-400 mb-2">Référentiels dominants</h5>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {pilier.referentiels.map((ref) => (
                          <span key={ref} className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded border border-purple-500/30">
                            {ref}
                          </span>
                        ))}
                      </div>

                      <h5 className="text-sm font-bold text-red-400 mb-2">Erreurs fréquentes</h5>
                      <ul className="space-y-2">
                        {pilier.erreursFrequentes.map((erreur, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                            <span>{erreur}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div>
                    <h5 className="text-sm font-bold text-indigo-300 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Niveaux 0 → 5
                    </h5>
                    <div className="space-y-2">
                      {pilier.niveaux.map((n) => (
                        <div key={n.niveau} className="bg-slate-800/50 rounded-lg p-3 border border-indigo-500/20">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-indigo-400">{n.niveau}</span>
                            <span className="text-xs text-gray-500">•</span>
                            <span className="text-xs font-semibold text-purple-300">{n.label}</span>
                          </div>
                          <p className="text-gray-300 text-xs leading-relaxed">{n.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h5 className="text-sm font-bold text-blue-400 mb-2">À faire maintenant</h5>
                      <ul className="space-y-2">
                        {pilier.aFaireMaintenant.map((action, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                            <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="text-sm font-bold text-emerald-400 mb-2">Preuves visibles</h5>
                      <div className="flex flex-wrap gap-2">
                        {pilier.preuvesVisibles.map((preuve, idx) => (
                          <span key={idx} className="px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded border border-emerald-500/30">
                            {preuve}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TransversalesTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-600/20 via-teal-600/20 to-emerald-600/20 rounded-xl p-6 border-2 border-emerald-500/40">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Les 2 dimensions transversales</h3>
        <p className="text-gray-300 text-center text-sm">
          Ce qui traverse tous les piliers plutôt que de devenir un silo supplémentaire.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {transversales.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.id} className="bg-white/5 rounded-xl p-6 border border-emerald-500/20 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h4 className="text-lg font-bold text-white">{item.nom}</h4>
              </div>

              <div>
                <h5 className="text-sm font-bold text-yellow-400 mb-2">Pourquoi transversal</h5>
                <p className="text-sm text-gray-300 leading-relaxed">{item.pourquoi}</p>
              </div>

              <div>
                <h5 className="text-sm font-bold text-cyan-400 mb-2">Ce que ça couvre</h5>
                <ul className="space-y-2">
                  {item.couvre.map((line, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h5 className="text-sm font-bold text-emerald-400 mb-2">Rôle dans le cadre</h5>
                <p className="text-sm text-gray-300 leading-relaxed">{item.role}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MaturiteTab() {
  const radarData = piliersCadreBZ.map((item) => ({
    category: item.nom.length > 18 ? item.nom.substring(0, 18) + '...' : item.nom,
    Actuel: (item.actuel / 5) * 100,
    Cible: (item.cible / 5) * 100,
  }));

  const barData = piliersCadreBZ.map((item) => ({
    axe: item.nom.length > 16 ? item.nom.substring(0, 16) + '...' : item.nom,
    Actuel: item.actuel,
    Cible: item.cible,
  }));

  const actuelAverage = useMemo(
    () => (piliersCadreBZ.reduce((sum, item) => sum + item.actuel, 0) / piliersCadreBZ.length).toFixed(1),
    [],
  );
  const cibleAverage = useMemo(
    () => (piliersCadreBZ.reduce((sum, item) => sum + item.cible, 0) / piliersCadreBZ.length).toFixed(1),
    [],
  );

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Maturité du cadre BZ</h3>
        <p className="text-gray-300 text-center text-sm">
          Vue radar et barres des 6 piliers principaux — lecture actuelle et cible recommandée.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
          <div className="text-sm text-yellow-300 font-medium">Score Actuel</div>
          <div className="text-2xl font-bold text-yellow-200">{actuelAverage}/5</div>
          <div className="text-xs text-yellow-400">Lecture initiale</div>
        </div>
        <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4">
          <div className="text-sm text-emerald-300 font-medium">Score Cible</div>
          <div className="text-2xl font-bold text-emerald-200">{cibleAverage}/5</div>
          <div className="text-xs text-emerald-400">Cadre stabilisé</div>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Radar — Vue d’ensemble</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
              <PolarGrid stroke="#4b5563" />
              <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#d1d5db' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} tickCount={6} />
              <Radar name="Actuel" dataKey="Actuel" stroke="#fbbf24" fill="#fbbf24" fillOpacity={0.2} strokeWidth={3} />
              <Radar name="Cible" dataKey="Cible" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={3} />
              <Legend wrapperStyle={{ color: '#d1d5db' }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Comparatif par pilier</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 20, right: 20, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="axe" tick={{ fill: '#d1d5db', fontSize: 11 }} angle={-15} textAnchor="end" height={60} />
              <YAxis domain={[0, 5]} tick={{ fill: '#d1d5db', fontSize: 11 }} />
              <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #4f46e5', borderRadius: '12px', color: '#fff' }} />
              <Bar dataKey="Actuel" fill="#fbbf24" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Cible" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function MappingTab() {
  const tactiquesSansTransversale = tactiquesAvecCadreSimple.filter((tactique) => tactique.transversaleIds.length === 0).length;
  const tactiquesAvecTransversale = tactiquesAvecCadreSimple.length - tactiquesSansTransversale;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-fuchsia-600/20 via-indigo-600/20 to-sky-600/20 rounded-xl p-6 border-2 border-fuchsia-500/40">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Mapping 6 + 2 → tactiques → livrables</h3>
        <p className="text-gray-300 text-center text-sm">
          Le nouveau cadre reste simple en surface, mais conserve toute la profondeur opérationnelle du cadre BZ.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Piliers simples', value: cadreBZMappingStats.piliers, color: 'from-fuchsia-500 to-violet-500' },
          { label: 'Dimensions transversales', value: cadreBZMappingStats.transversales, color: 'from-emerald-500 to-teal-500' },
          { label: 'Tactiques mappées', value: cadreBZMappingStats.tactiques, color: 'from-sky-500 to-blue-500' },
          { label: 'Livrables rattachés', value: cadreBZMappingStats.livrables, color: 'from-amber-500 to-orange-500' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center mb-3`}>
              <Layers3 className="w-5 h-5 text-white" />
            </div>
            <div className="text-3xl font-black text-white">{stat.value}</div>
            <div className="text-xs text-gray-400 mt-1">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
          <h4 className="text-xl font-bold text-white mb-4">Lecture client</h4>
          <div className="space-y-3 text-sm text-gray-300">
            <p>
              Le client voit d’abord un cadre facile à comprendre: 6 piliers et 2 dimensions transversales.
              Cette couche sert à expliquer le territoire sans l’écraser avec trop de détails.
            </p>
            <p>
              Derrière chaque pilier, PRIsME conserve les tactiques et les livrables tangibles. Le cadre devient donc
              présentable, exécutable et mesurable.
            </p>
          </div>
        </div>

        <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
          <h4 className="text-xl font-bold text-white mb-4">Lecture interne BZ</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-900/60 p-4 border border-slate-700">
              <div className="text-2xl font-bold text-cyan-300">{tactiquesAvecTransversale}</div>
              <div className="text-xs text-gray-400">tactiques avec dimension transverse</div>
            </div>
            <div className="rounded-lg bg-slate-900/60 p-4 border border-slate-700">
              <div className="text-2xl font-bold text-slate-200">{tactiquesSansTransversale}</div>
              <div className="text-xs text-gray-400">tactiques strictement pilier</div>
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-400">
            Les livrables suivent automatiquement la tactique source, donc le catalogue reste cohérent sans double saisie.
          </div>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Structure cible</h4>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
          <div className="rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/30 p-4">
            <div className="font-bold text-fuchsia-300 mb-2">1. Cadre 6 + 2</div>
            <p className="text-gray-300">Langage simple pour diagnostic, vente, comité et gouvernance.</p>
          </div>
          <div className="rounded-lg bg-sky-500/10 border border-sky-500/30 p-4">
            <div className="font-bold text-sky-300 mb-2">2. Tactiques</div>
            <p className="text-gray-300">Actions activables pour faire progresser ASIS vers TARGET.</p>
          </div>
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-4">
            <div className="font-bold text-amber-300 mb-2">3. Livrables</div>
            <p className="text-gray-300">Preuves tangibles remises au client et suivies dans PRIsME.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TactiquesMappingTab() {
  const grouped = useMemo(
    () =>
      cadreBZSimplePiliers.map((pilier) => {
        const tactiques = tactiquesAvecCadreSimple.filter((tactique) => tactique.simplePilierId === pilier.id);
        const livrablesCount = livrablesAvecCadreSimple.filter((livrable) => livrable.simplePilierId === pilier.id).length;
        return { ...pilier, tactiques, livrablesCount };
      }),
    [],
  );

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-sky-600/20 via-blue-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-sky-500/40">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Tactiques associées au cadre simple</h3>
        <p className="text-gray-300 text-center text-sm">
          Les tactiques du cadre BZ complet sont reclassées derrière les 6 piliers et enrichies avec les dimensions transversales.
        </p>
      </div>

      <div className="space-y-4">
        {grouped.map((pilier) => (
          <div key={pilier.id} className="bg-white/5 rounded-xl border border-indigo-500/20 overflow-hidden">
            <div className="p-5 bg-slate-900/60 border-b border-indigo-500/20">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div>
                  <h4 className="text-lg font-bold text-white">{pilier.nom}</h4>
                  <p className="text-sm text-gray-400 mt-1">{pilier.description}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <span className="px-3 py-1 rounded-full bg-sky-500/20 text-sky-200 text-xs font-bold border border-sky-500/30">
                    {pilier.tactiques.length} tactiques
                  </span>
                  <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-200 text-xs font-bold border border-amber-500/30">
                    {pilier.livrablesCount} livrables
                  </span>
                </div>
              </div>
            </div>

            <div className="divide-y divide-indigo-500/10">
              {pilier.tactiques.map((tactique) => {
                const livrablesCount = livrablesAvecCadreSimple.filter((livrable) => livrable.tactiqueId === tactique.id).length;
                return (
                  <div key={tactique.id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-black text-sky-300">{tactique.id}</span>
                          <span className="text-sm font-bold text-white">{tactique.nom}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{tactique.description}</p>
                        {tactique.transversaleIds.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {tactique.transversaleIds.map((id) => (
                              <span key={id} className="px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-200 text-[11px] border border-emerald-500/30">
                                {getTransversaleName(id)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="px-2 py-1 rounded-full bg-white/10 text-gray-200 text-[11px] border border-white/10">
                          Ancien pilier {tactique.pilier}
                        </span>
                        <span className="px-2 py-1 rounded-full bg-amber-500/15 text-amber-200 text-[11px] border border-amber-500/30">
                          {livrablesCount} livrables
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LivrablesMappingTab() {
  const [selectedPilierId, setSelectedPilierId] = useState<SimpleCadreBZPilierId>(cadreBZSimplePiliers[0].id);
  const selectedLivrables = livrablesAvecCadreSimple.filter((livrable) => livrable.simplePilierId === selectedPilierId);
  const selectedTactiques = tactiquesAvecCadreSimple.filter((tactique) => tactique.simplePilierId === selectedPilierId);
  const standards = Array.from(new Set(selectedLivrables.map((livrable) => livrable.standard))).sort();
  const types = Array.from(new Set(selectedLivrables.map((livrable) => livrable.type))).sort();

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-amber-600/20 via-orange-600/20 to-rose-600/20 rounded-xl p-6 border-2 border-amber-500/40">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Livrables tangibles associés</h3>
        <p className="text-gray-300 text-center text-sm">
          Les livrables suivent leur tactique source et deviennent la preuve concrète de progression du cadre.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {cadreBZSimplePiliers.map((pilier) => {
          const livrablesCount = livrablesAvecCadreSimple.filter((livrable) => livrable.simplePilierId === pilier.id).length;
          const tactiquesCount = tactiquesAvecCadreSimple.filter((tactique) => tactique.simplePilierId === pilier.id).length;
          const isSelected = selectedPilierId === pilier.id;
          return (
            <button
              key={pilier.id}
              type="button"
              onClick={() => setSelectedPilierId(pilier.id)}
              className={`text-left rounded-xl p-4 border transition-colors ${
                isSelected
                  ? 'bg-amber-500/20 border-amber-400/60 shadow-lg shadow-amber-500/10'
                  : 'bg-white/5 border-indigo-500/20 hover:bg-white/10'
              }`}
            >
              <div className="text-sm font-bold text-white">{pilier.nom}</div>
              <div className="mt-2 flex gap-2 text-[11px]">
                <span className="px-2 py-1 rounded-full bg-sky-500/20 text-sky-200">{tactiquesCount} tactiques</span>
                <span className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-200">{livrablesCount} livrables</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
          <div>
            <h4 className="text-xl font-bold text-white">{getSimplePilierName(selectedPilierId)}</h4>
            <p className="text-sm text-gray-400 mt-1">
              {selectedTactiques.length} tactiques alimentent {selectedLivrables.length} livrables tangibles.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {standards.slice(0, 8).map((standard) => (
              <span key={standard} className="px-2 py-1 rounded-full bg-violet-500/15 text-violet-200 text-[11px] border border-violet-500/30">
                {standard}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <div className="rounded-lg bg-slate-900/60 p-4 border border-slate-700">
            <div className="text-2xl font-bold text-amber-200">{selectedLivrables.length}</div>
            <div className="text-xs text-gray-400">livrables</div>
          </div>
          <div className="rounded-lg bg-slate-900/60 p-4 border border-slate-700">
            <div className="text-2xl font-bold text-sky-200">{selectedTactiques.length}</div>
            <div className="text-xs text-gray-400">tactiques sources</div>
          </div>
          <div className="rounded-lg bg-slate-900/60 p-4 border border-slate-700">
            <div className="text-2xl font-bold text-violet-200">{standards.length}</div>
            <div className="text-xs text-gray-400">standards détectés</div>
          </div>
          <div className="rounded-lg bg-slate-900/60 p-4 border border-slate-700">
            <div className="text-2xl font-bold text-emerald-200">{types.length}</div>
            <div className="text-xs text-gray-400">types de livrables</div>
          </div>
        </div>

        <div className="space-y-3">
          {selectedLivrables.slice(0, 24).map((livrable) => (
            <div key={livrable.id} className="rounded-lg bg-slate-900/60 border border-slate-700 p-4">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div>
                  <div className="text-sm font-bold text-white">{livrable.livrableNom}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {livrable.tactiqueId} · {livrable.tactiqueNom}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <span className="px-2 py-1 rounded-full bg-sky-500/15 text-sky-200 text-[11px] border border-sky-500/30">
                    {livrable.type}
                  </span>
                  <span className="px-2 py-1 rounded-full bg-violet-500/15 text-violet-200 text-[11px] border border-violet-500/30">
                    {livrable.standard}
                  </span>
                  <span className="px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-200 text-[11px] border border-emerald-500/30">
                    {livrable.profondeur}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedLivrables.length > 24 && (
          <div className="mt-4 text-xs text-gray-400">
            Affichage des 24 premiers livrables pour garder la vue lisible. Le mapping complet est disponible dans les données.
          </div>
        )}
      </div>
    </div>
  );
}

export default function EbaucheCadreBZModal({ isOpen, onClose }: EbaucheCadreBZModalProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'piliers' | 'transversales' | 'mapping' | 'tactiques' | 'livrables' | 'maturite'>('overview');

  const tabs = [
    { id: 'overview' as const, label: 'Esprit du cadre', icon: Layers3 },
    { id: 'piliers' as const, label: '6 piliers', icon: Target },
    { id: 'transversales' as const, label: 'Transversales', icon: Users },
    { id: 'mapping' as const, label: 'Mapping', icon: Layers3 },
    { id: 'tactiques' as const, label: 'Tactiques', icon: CheckCircle },
    { id: 'livrables' as const, label: 'Livrables', icon: BookOpen },
    { id: 'maturite' as const, label: 'Maturité', icon: TrendingUp },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl w-[90vw] h-[90vh] border border-indigo-500/30 shadow-2xl shadow-indigo-500/20 flex flex-col">
              <div className="relative p-3 border-b border-indigo-500/30 bg-gradient-to-r from-indigo-900/50 to-purple-900/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-gradient-to-br from-fuchsia-500 to-violet-500 rounded-lg">
                      <Layers3 className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-violet-400">
                        Ébauche de cadre client BZ
                      </h2>
                      <p className="text-gray-400 text-xs">
                        Cadre unifié BZ aligné ITIL, COBIT, NIST et PRIsME
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors ml-2">
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="border-b border-indigo-500/30 bg-slate-900/50 px-6 pt-3 pb-0 overflow-x-auto">
                <div className="flex gap-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold text-sm transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-b from-indigo-600 to-purple-600 text-white shadow-lg'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <AnimatePresence mode="wait">
                  {activeTab === 'overview' && <OverviewTab />}
                  {activeTab === 'piliers' && <PiliersTab />}
                  {activeTab === 'transversales' && <TransversalesTab />}
                  {activeTab === 'mapping' && <MappingTab />}
                  {activeTab === 'tactiques' && <TactiquesMappingTab />}
                  {activeTab === 'livrables' && <LivrablesMappingTab />}
                  {activeTab === 'maturite' && <MaturiteTab />}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
